import Link from 'next/link';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ContentWrapper } from '@/components/b2b/shared/styles';
import { useAnalytics } from '@/hooks/analytics';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';

import MainTextContentCtaColumn from '../MainTextContentCtaColumn';
import { IntegrationType, SignupScreenProps } from '../shared/types';
import messages from './messages';
import {
	CustomSSOLink,
	ErrorHeader,
	ErrorSupport,
	MaxSubscriptionHeader,
	PaddedTop,
	StyledText,
} from './styles';

const Error = ({
	error,
	supportUrl,
	partnerDetails,
	isRevampRedempFlow,
	onScreenChange,
}: Pick<
	SignupScreenProps,
	'error' | 'supportUrl' | 'partnerDetails' | 'isRevampRedempFlow' | 'onScreenChange'
>) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Error : Landed',
			eventProps: {
				error: error?.code ?? null,
			},
		});
	});

	function getSupportLink(): string {
		return (
			supportUrl ??
			(error?.partnerSlug === 'kp'
				? 'https://support.calm.com/hc/en-us/articles/360047041934-Received-an-error-message-while-activating-my-Kaiser-subscription'
				: 'https://support.calm.com/hc/en-us/articles/360055261934')
		);
	}

	function getErrorMessage(): string | ReactNode | null {
		if (!error?.code) return null;
		switch (error?.code) {
			case 'b2b_partner_requires_data_sharing':
				return formatMessage(messages.dataSharingRequired, {
					partnerName: partnerDetails?.name,
					supportlink: (...chunks: ReactNode[]) => <Link href="/help">{chunks}</Link>,
				});
			case 'b2b_partner_not_found':
				const message = isRevampRedempFlow
					? formatMessage(messages.partnerNotFoundRevamp)
					: formatMessage(messages.partnerNotFound);
				return message;
			case 'b2b_partner_not_active':
				return formatMessage(messages.partnerNotActive);
			case 'b2b_sales_partner_exceeded_max_subscriptions':
				return formatMessage(messages.salesPartnerExceededMaxSubscriptions);
			case 'b2b_partner_exceeded_max_subscriptions':
				const content = (
					<div>
						<MaxSubscriptionHeader>
							{formatMessage(messages.groupCodePartnerExceededMaxSubscriptionsHeader)}
						</MaxSubscriptionHeader>
						{formatMessage(messages.groupCodePartnerExceededMaxSubscriptions)}
					</div>
				);
				return content;
			case 'b2b_user_has_existing_subscription':
				return formatMessage(messages.userHasSubscription, {
					managesubscriptionlink: (...chunks: ReactNode[]) => (
						<a href="/profile/manage-subscription" target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
				});
			case 'invalid_token':
				return formatMessage(messages.invalidToken, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			case 'b2b_partner_eligibility_redeem_invalid_id':
				if (partnerDetails.integration_type === IntegrationType.API) {
					return formatMessage(messages.invalidRedeemIdApi, {
						supportlink: (...chunks: ReactNode[]) => (
							<a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
								{chunks}
							</a>
						),
						partnerName: partnerDetails?.name,
					});
				}
				return partnerDetails.pageInfo.messages.error
					? formatMessage(partnerDetails.pageInfo.messages.error)
					: formatMessage(messages.invalidRedeemId);
			case 'sso_redirect': {
				// TODO: The 'customSSOLinks' approach is a one-off to satisfy a valuable partner. Long term, Partner Portal should be updated to accommodate multiple SSO Links for a consistent experience.
				const customSSOLinks = partnerDetails.pageInfo?.customSSOLinks;
				const content = customSSOLinks
					? formatMessage(messages.ssoMissingTokenMultipleLinks, {
							partnerName: `${partnerDetails?.name}`,
							links: (
								<>
									{Object.values(customSSOLinks).map(({ label, url }) => (
										<CustomSSOLink key={url}>
											<a href={url}>{label.defaultMessage}</a>
										</CustomSSOLink>
									))}
								</>
							),
						})
					: formatMessage(
							partnerDetails.web_renew_url
								? messages.ssoMissingTokenFriendly
								: messages.ssoMissingTokenNoRenewUrl,
							{
								partnerName: `${partnerDetails?.name}`,
								anchor: (...chunks: ReactNode[]) => <a href={partnerDetails.web_renew_url}>{chunks}</a>,
							},
						);
				return content;
			}
			case 'b2b_partner_subscription_upsert_user_conflict':
				return formatMessage(messages.alreadyRedeemed, {
					supportlink: (...chunks: ReactNode[]) => (
						<a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
							{chunks}
						</a>
					),
					partnerName: partnerDetails?.name,
				});
			case 'b2b_partner_group_code_mismatch':
				return formatMessage(messages.groupCodeMismatch);
			default:
				return formatMessage(messages.errorCode, {
					error: (
						<PaddedTop>
							<b>{error.code}</b>
						</PaddedTop>
					),
				});
		}
	}

	function isCtaVisible(): boolean {
		if (!error?.code) return false;
		if (partnerDetails?.integration_type === IntegrationType.API) return false;
		switch (error?.code) {
			case 'generic_error':
				return false;
			case 'b2b_partner_requires_data_sharing':
				return false;
			case 'b2b_partner_not_found':
				return false;
			case 'b2b_partner_not_active':
				return false;
			case 'sso_redirect':
				return false;
			default:
				return true;
		}
	}

	const onCtaPress = () => {
		logEvent({
			eventName: 'Error : CTA Clicked',
			eventProps: {
				error: error?.code ?? null,
			},
		});
		onScreenChange('eligibility');
	};

	const handledErrorMessage = getErrorMessage();

	return (
		<ContentWrapper isRevampRedempFlow={isRevampRedempFlow}>
			{
				// DK - disabling Oops message for sso_redirect errors, as partners found it confusing
				error?.code !== 'sso_redirect' && !isRevampRedempFlow && (
					<ErrorHeader>{formatMessage(messages.sorry)}</ErrorHeader>
				)
			}
			{handledErrorMessage &&
				(isRevampRedempFlow ? (
					<MainTextContentCtaColumn
						title={error?.code !== 'sso_redirect' ? formatMessage(messages.sorry) : ''}
						buttonProps={
							isCtaVisible()
								? { buttonText: formatMessage(messages.backButton), onPress: onCtaPress }
								: undefined
						}
						isAppStoreButtonsVisible={false}
						isAdditionalPaddingAboveCtaButton={true}
					>
						<StyledText>{handledErrorMessage}</StyledText>
					</MainTextContentCtaColumn>
				) : (
					<ErrorSupport>{handledErrorMessage}</ErrorSupport>
				))}
			{error && error.partner_user_id && (
				<PaddedTop>
					{formatMessage(messages.participantId, {
						pid: <b>{error.partner_user_id}</b>,
					})}
				</PaddedTop>
			)}
		</ContentWrapper>
	);
};

export default Error;
